import { parseSemVer, isMajorUpgrade, isMinorUpgrade } from "./versionResolver.js";
import { RN_EXPO_MATRIX, PACKAGE_COMPAT_MATRIX } from "./compatibilityMatrix.js";
import type { MigrationPlan, MigrationPhase, MigrationStep } from "../models/MigrationPlan.js";

function stepIndex(order: number, description: string, command?: string, required = true): MigrationStep {
  return { order, description, command, required };
}

/**
 * Generates a structured migration plan between two React Native versions.
 * All logic is offline — no network calls.
 */
export function generateMigrationPlan(fromVersion: string, toVersion: string): MigrationPlan {
  const from = parseSemVer(fromVersion);
  const to = parseSemVer(toVersion);

  if (!from || !to) {
    throw new Error(`Invalid versions: "${fromVersion}" → "${toVersion}"`);
  }

  const fromMinor = `${from.major}.${from.minor}`;
  const toMinor = `${to.major}.${to.minor}`;

  const phases: MigrationPhase[] = [];
  const dependencyUpdates: MigrationPlan["dependencyUpdates"] = [];
  const pluginUpdates: MigrationPlan["pluginUpdates"] = [];
  const syncOperations: string[] = [];
  const validationSteps: string[] = [];

  // ── Phase 1: Pre-flight ────────────────────────────────────────────────────
  phases.push({
    name: "Pre-flight",
    description: "Back up your project and prepare for the migration",
    steps: [
      stepIndex(1, "Create a project snapshot", "kyro snapshot create"),
      stepIndex(2, "Run diagnostics to record current state", "kyro doctor"),
      stepIndex(3, "Commit or stash all pending changes", "git stash", true),
      stepIndex(4, "Verify CI is green on the current version", undefined, false),
    ],
  });

  // ── Phase 2: Dependency Updates ───────────────────────────────────────────
  const depUpdateSteps: MigrationStep[] = [];
  let stepNum = 1;

  // Collect package compat matrix recommendations
  const affectedPackages = PACKAGE_COMPAT_MATRIX.filter((entry) => {
    return entry.minVersionByRN[toMinor] !== undefined;
  });

  for (const entry of affectedPackages) {
    const minVer = entry.minVersionByRN[toMinor];
    if (!minVer) continue;
    dependencyUpdates.push({
      name: entry.packagePrefix,
      from: `<${minVer}`,
      to: minVer,
    });
    depUpdateSteps.push(
      stepIndex(
        stepNum++,
        `Update ${entry.packagePrefix} to ^${minVer}`,
        `npm install ${entry.packagePrefix}@^${minVer}`,
        false
      )
    );
  }

  // Core RN update step
  depUpdateSteps.unshift(
    stepIndex(0, `Update react-native to ${toVersion}`, `npm install react-native@${toVersion}`, true)
  );

  phases.push({
    name: "Dependency Updates",
    description: `Update react-native and ecosystem packages to versions compatible with ${toVersion}`,
    steps: depUpdateSteps,
  });

  // ── Phase 3: Native Project Updates ───────────────────────────────────────
  const nativeSteps: MigrationStep[] = [];
  const needsPodInstall = true; // Always run pod install after RN upgrade
  const needsGradleSync = true;

  if (isMajorUpgrade(fromVersion, toVersion) || isMinorUpgrade(fromVersion, toVersion)) {
    const targetEntry = RN_EXPO_MATRIX.find((e) => e.rnMinor === toMinor);

    if (targetEntry?.breakingChanges?.length) {
      targetEntry.breakingChanges.forEach((change, i) => {
        nativeSteps.push(stepIndex(i + 1, `Review breaking change: ${change}`, undefined, true));
      });
    }

    nativeSteps.push(
      stepIndex(nativeSteps.length + 1, "Run React Native upgrade helper", `npx react-native upgrade`, false),
      stepIndex(nativeSteps.length + 2, "Review and apply template diffs from the upgrade", undefined, true),
      stepIndex(nativeSteps.length + 3, "Update android/build.gradle and app/build.gradle if needed", undefined, true),
      stepIndex(nativeSteps.length + 4, "Update ios/Podfile if needed", undefined, true)
    );
  }

  phases.push({
    name: "Native Project Updates",
    description: "Apply changes to native iOS and Android project files",
    steps: nativeSteps.length ? nativeSteps : [
      stepIndex(1, "Review native project files for any manual adjustments", undefined, false),
    ],
  });

  // ── Phase 4: Sync ──────────────────────────────────────────────────────────
  syncOperations.push("npm install (or yarn/pnpm install)");
  syncOperations.push("pod install (iOS)");
  if (needsGradleSync) syncOperations.push("Gradle sync (Android)");

  phases.push({
    name: "Synchronization",
    description: "Reinstall all dependencies and run platform sync steps",
    steps: [
      stepIndex(1, "Install all dependencies", "kyro sync"),
      stepIndex(2, "Install iOS CocoaPods", "pod install --project-directory=ios", needsPodInstall),
    ],
  });

  // ── Phase 5: Validation ───────────────────────────────────────────────────
  validationSteps.push("kyro doctor — verify environment");
  validationSteps.push("kyro analyze — check project health score");
  validationSteps.push("Run your test suite");
  validationSteps.push("Build and run on iOS simulator");
  validationSteps.push("Build and run on Android emulator");

  phases.push({
    name: "Validation",
    description: "Verify the project builds and runs correctly on both platforms",
    steps: validationSteps.map((v, i) => stepIndex(i + 1, v, undefined, i < 2)),
  });

  // Estimate complexity
  const minorDiff = to.minor - from.minor;
  const estimatedComplexity: MigrationPlan["estimatedComplexity"] =
    isMajorUpgrade(fromVersion, toVersion)
      ? "HIGH"
      : minorDiff > 2
      ? "MEDIUM"
      : "LOW";

  return {
    fromVersion,
    toVersion,
    phases,
    dependencyUpdates,
    pluginUpdates,
    syncOperations,
    podInstalls: needsPodInstall,
    validationSteps,
    estimatedComplexity,
    generatedAt: new Date().toISOString(),
  };
}
